import React, { useState, useEffect, useCallback, useContext, useMemo, Suspense } from "react";
import { TextField, Button } from "@mui/material/";
import PropTypes from "prop-types";
import { padding } from "@mui/system";
import PersonalInformation from "./personalInformation/PersonalInformation";
import BenefitSelection from "./benefitSelection/BenefitSelection";
import HeaderInformation from "./headerInformation/HeaderInformation";
import EmployerSection from "./employerSection/EmployerSection";
import SendIcon from "@mui/icons-material/Send";
import { useHistory, useLocation } from "react-router-dom";
import FormSingleAction from "../../../../../shared/enrollment/formSingleAction/FormSingleAction";

import "./lincolnForm.css";
import { PlanFormProps } from "../../../../../../types/components/enrollmentProps.types";
import PlanActions from "../../../../../shared/enrollment/planActions/PlanActions";
import { PremiumRateCoverage } from "../../../../../../types/plan.types";
import { dollarize } from "../../../../../../utils/commonFunctions/dollarize";
import { AuthContext, GroupEnrollmentContext, GroupOwnerContext, PaycheckContext } from "../../../../../../contexts";
import { api } from "../../../../../../utils/api";
import { getCurrentDate } from "../../../../../../utils/commonFunctions/date";
import { CommonDetails } from "../../../../../../types/enrollment.types";
import { AddEnrollmentPayload } from "../../../../../../utils/api/enrollment/enrollment";
import { CustomFormDialogType } from "../../../../../../types/components/dialogProps.types";
import {
	generateGLTDClass2WaiveEnrollmentPayload,
	generateGSTDWaiveEnrollmentPayload
} from "../../../../../../utils/commonFunctions/groupEnrollment/enrollment";
import { LazyCustomFormDialog } from "../../../../../shared";
import { trackPromise } from "react-promise-tracker";

const LincolnForm = ({ getEnrollmentInitData, openEnrollment, isActiveEnrollment }: PlanFormProps) => {
	const history = useHistory();
	const [hasConfirmedEnrollment, setHasConfirmedEnrollment] = useState(false);
	const location = useLocation();
	const { setCurrentEnrollment } = useContext(GroupEnrollmentContext);
	const { member } = useContext(AuthContext);
	const [premiumAmount, setPremiumAmount] = useState(0);
	const [waiveReason, setWaiveReason] = useState("");
	const [isWaiveEnrollmentTriggered, setIsWaiveEnrollmentTriggered] = useState(false);
	const { paycheck } = useContext(PaycheckContext);
	const { groupOwner } = useContext(GroupOwnerContext);

	const urlSearchParams: URLSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

	const handleWaiveReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setWaiveReason(value);
	};

	const [customFormDialogProps, setCustomFormDialogProps] = useState<CustomFormDialogType>();

	const handleWaiveEnrollment = useCallback(async () => {
		if (member === null || openEnrollment === null) {
			return;
		}
		const { standardDetails, commonDetails } = generateGLTDClass2WaiveEnrollmentPayload(
			member,
			openEnrollment,
			waiveReason
		);

		if (isActiveEnrollment) {
			commonDetails.enrollment_status = "WAIVED - WAITING FOR APPROVAL";
			// debugger;
		}

		const enrollmentPayload: AddEnrollmentPayload = {
			standard_details: standardDetails,
			common_details: { ...commonDetails, agent_id: groupOwner && groupOwner._id ? groupOwner._id : null }
		};
		const response = await api.enrollment.addEnrollment(enrollmentPayload);
		if (response) {
			getEnrollmentInitData(false);
		}
		const step = urlSearchParams.get("step");
		history.push(`?step=${step}&stage=0`);
	}, [getEnrollmentInitData, history, member, openEnrollment, urlSearchParams, waiveReason]);

	const handleWaiveButtonClick = useCallback(() => {
		setCustomFormDialogProps(
			Object.assign({}, customFormDialogProps, {
				openDialog: true,
				title: "Are you sure want to waive this enrollment?",
				textfields: [
					{
						label: "Waive Reason",
						onChange: handleWaiveReasonChange,
						value: waiveReason,
						placeholder: "Enter your reason for waive"
					}
				],
				actions: [
					{
						label: "Cancel",
						callback: () =>
							setCustomFormDialogProps(Object.assign({}, customFormDialogProps, { openDialog: false }))
					},
					{
						label: "Yes",
						callback: () => {
							setCustomFormDialogProps(
								Object.assign({}, customFormDialogProps, {
									openDialog: false
								})
							);
							// handleWaiveEnrollment();
							setIsWaiveEnrollmentTriggered(true);
						}
					}
				]
			})
		);
	}, [customFormDialogProps, handleWaiveEnrollment, waiveReason]);

	const getPremiumRateByFixed = useCallback(
		(basePayRate: number, coverageCode: PremiumRateCoverage): number | null => {
			if (paycheck === null) {
				return null;
			}
			let premiumAmount: number | null = null;
			if (paycheck.pay_frequency === "MONTHLY") {
				premiumAmount = coverageCode.monthly_premium as number;
			} else if (paycheck.pay_frequency === "WEEKLY") {
				premiumAmount = coverageCode.weekly_premium as number;
			}
			return premiumAmount;
		},
		[paycheck]
	);

	const getPremiumRateByPercentage = useCallback(
		(basePayRate: number, coverageCode: PremiumRateCoverage): number | null => {
			if (paycheck === null) {
				return null;
			}
			let premiumAmount: number | null = null;
			if (paycheck.pay_frequency === "MONTHLY") {
				premiumAmount = (basePayRate * ((coverageCode.monthly_premium as number) / 100)) as number;
				if (premiumAmount > (coverageCode.monthly_maximum_premium as number)) {
					premiumAmount = coverageCode.monthly_maximum_premium as number;
				} else if (premiumAmount < (coverageCode.monthly_minimum_premium as number)) {
					premiumAmount = coverageCode.monthly_minimum_premium as number;
				}
			} else if (paycheck.pay_frequency === "WEEKLY") {
				premiumAmount = (basePayRate * ((coverageCode.weekly_premium as number) / 100)) as number;
				if (premiumAmount > (coverageCode.weekly_maximum_premium as number)) {
					premiumAmount = coverageCode.weekly_maximum_premium as number;
				} else if (premiumAmount < (coverageCode.weekly_minimum_premium as number)) {
					premiumAmount = coverageCode.weekly_minimum_premium as number;
				}
			}
			return premiumAmount;
		},
		[paycheck]
	);

	const calculatePremiumByCoverage = useCallback(
		(basePayRate: number, coverageCode: PremiumRateCoverage): number | null => {
			if (openEnrollment === null || paycheck === null) {
				return null;
			}

			let premiumAmount: number | null;
			if (coverageCode.active === false) {
				alert("Member is not covered in this plan");
				return null;
			}
			if (coverageCode.premium_type === "PERCENTAGE") {
				premiumAmount = getPremiumRateByPercentage(basePayRate, coverageCode);
			} else if (coverageCode.premium_type === "FIXED") {
				premiumAmount = getPremiumRateByFixed(basePayRate, coverageCode);
			} else {
				alert("Invalid premium type");
				return null;
			}
			return premiumAmount;
		},
		[getPremiumRateByFixed, getPremiumRateByPercentage, openEnrollment, paycheck]
	);

	const calculatePremium = useCallback(() => {
		if (openEnrollment === null || paycheck === null) {
			return null;
		}

		let basePayRate: number;
		if (paycheck?.pay_frequency === "WEEKLY") {
			basePayRate = paycheck.weekly_base_rate;
		} else if (paycheck?.pay_frequency === "MONTHLY") {
			basePayRate = paycheck.monthly_base_rate;
		} else {
			alert("Invalid Pay Frequency");
			return;
		}
		let employeePremium: number | null = 0;
		employeePremium = calculatePremiumByCoverage(basePayRate, openEnrollment.plan.coverage.EE);

		if (employeePremium === null) {
			return;
		}
		setPremiumAmount(employeePremium);
		if (openEnrollment) {
			// setCurrentEnrollment(
			// 	Object.assign(
			// 		{},
			// 		{
			// 			plan_name: openEnrollment.plan.plan_name,
			// 			status: "Current",
			// 			premium_amount: employeePremium
			// 		}
			// 	)
			// );
		}
	}, [calculatePremiumByCoverage, openEnrollment, paycheck]);

	const handleConfirmEnrollmentChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setHasConfirmedEnrollment(event.target.checked);
	}, []);

	const handleActivateEnrollment = useCallback(async () => {
		if (member === null || openEnrollment === null || paycheck === null || premiumAmount === null) {
			return;
		}

		if (hasConfirmedEnrollment === false) {
			alert("Please confirm enrollment to proceed");
			return;
		}

		const enrollmentCommonDetails: CommonDetails = {
			agent_id: groupOwner && groupOwner._id ? groupOwner._id : null,
			location_number: member.location.location_number,
			location_name: member.location.location_name,
			group_number: member.group_number,
			group_name: member.group.name,
			plan_object_id: openEnrollment.plan._id,
			plan_code: openEnrollment.plan.plan_code,
			plan_name: openEnrollment.plan.plan_name,
			base_plan_name: openEnrollment.plan.plan_name,
			insured_object_id: member._id,
			insured_SSN: member.SSN,
			writing_number: 0,
			unenrolled_reason: null,
			waive_reason: null,
			termination_reason: null,
			enrollment_date: getCurrentDate(),
			effective_date: null,
			termination_date: null,
			open_enrollment_id: openEnrollment._id,
			total_premium: premiumAmount
		};
		const enrollmentPayload: AddEnrollmentPayload = {
			common_details: enrollmentCommonDetails,
			standard_details: [
				{
					member_object_id: member._id,
					member_SSN: member.SSN,
					coverage_code: "EE",
					premium_amount: premiumAmount,
					is_tobacco: false
				}
			]
		};
		const response = await trackPromise(api.enrollment.addEnrollment(enrollmentPayload));
		if (response) {
			const step = urlSearchParams.get("step");
			getEnrollmentInitData(false);
			history.push(`?step=${step}&stage=0`);
		}
	}, [
		getEnrollmentInitData,
		hasConfirmedEnrollment,
		history,
		member,
		openEnrollment,
		paycheck,
		premiumAmount,
		urlSearchParams
	]);

	useEffect(() => {
		calculatePremium();
	}, [calculatePremium]);

	useEffect(() => {
		if (isWaiveEnrollmentTriggered) {
			handleWaiveEnrollment();
			setIsWaiveEnrollmentTriggered(false); // Reset the trigger
		}
	}, [isWaiveEnrollmentTriggered, handleWaiveEnrollment]);

	if (openEnrollment === null) {
		return <></>;
	}

	return (
		<div className="lincoln-form" id="lincoln-form">
			<Suspense fallback={<div />}>
				{customFormDialogProps ? <LazyCustomFormDialog customFormDialogProps={customFormDialogProps} /> : null}
			</Suspense>
			<div className="header-information" id="header-information">
				<HeaderInformation openEnrollment={openEnrollment} />
			</div>
			<div className="header-container" id="header-container">
				<div className="form-header" id="form-header">
					<span className="header-number" id="header-number">
						1.
					</span>
					Your Personal Information
				</div>
				<div className="personal-informations" id="personal-informations">
					<PersonalInformation />
				</div>
			</div>
			<div className="header-container" id="header-container">
				<EmployerSection />
			</div>
			<div className="header-container" id="header-container">
				<div className="form-header" id="form-header">
					<span className="header-number" id="header-number">
						2.
					</span>
					Benefit Selection - Choose your benefits
				</div>
				<div className="benefit-selections" id="benefit-selections">
					<BenefitSelection premiumAmount={premiumAmount} />
				</div>
			</div>
			<div className="header-container" id="header-container">
				<div className="form-header" id="form-header">
					<span className="header-number" id="header-number">
						3.
					</span>
					Confirm Enrollment
				</div>
				<p className="warning-container">
					This group insurance has been offered to me and after careful consideration of the benefits, I have
					decided to:
				</p>
				<div className="checkbox-container">
					<span className="checkbox">
						<input
							type="checkbox"
							id="enroll_insurance"
							name="enroll_insurance"
							checked={hasConfirmedEnrollment}
							onChange={handleConfirmEnrollmentChange}
						/>
					</span>
					<span className="enroll-text">ENROLL FOR INSURANCE for which I am or may become eligible </span>
					<span className="enroll-text1">under the group policies issued by The Lincoln National Life </span>
					<p className="paragraph-text2">
						Insurance Company, or its insurance partners. If contributions are required, I authorize my
						Employer to deduct premium from my pay.
					</p>
				</div>
			</div>
			<div className="header-container" id="header-container">
				<div className="form-header" id="form-header">
					<span className="header-number" id="header-number">
						4.
					</span>
					Fraud Warning/State Disclosure(s)
				</div>
				<p className="warning-container">
					ANY PERSONWHO KNOWINGLY PRESENTS A FALSE OR FRAUDULENT CLAIM FOR THE PAYMENT OF A LOSS IS GUILTY OF
					A CRIME AND MAY BE SUBJECT TO FINES AND CONFINEMENT IN STATE PRISON.
				</p>
			</div>
			<div className="header-container" id="header-container">
				<div className="form-header" id="form-header">
					<span className="header-number" id="header-number">
						5.
					</span>
					Sign & Return
				</div>
				<p className="paragraph-container">
					I understand the group insurance requested will not be effective until approved by the Group
					Insurance Service Office of The Lincoln National Life Insurance Company, or its insurance partners.
					A delayed effective date will apply if you are not Actively at Work/an Active Member. A delayed
					effective date may apply to your dependent, if he or she is confirmed in a hospital or health care
					facility or is in a period of limited actively on the date insurance would otherwise take effect.
				</p>
				<p className="paragraph-container">
					I understand the information provided is for enrollment in group insurance as offered by my Employer
					and will not be used for underwriting purposes.
				</p>
				<p className="paragraph-container">
					The information provided is complete, true, and accurate to the best of my knowledge.
				</p>
				<div className="fullname-container">
					<span className="fullname-header">Your Full Name (Print):</span>
					<span className="textfield-container">
						<TextField
							className="text-field"
							id="text-field"
							variant="standard"
							name="full_name"
							style={{
								width: "700px",
								borderRadius: 50
							}}
							value={member?.first_name + " " + member?.last_name}
							InputProps={{
								readOnly: true
							}}
						/>
					</span>
				</div>
				<div className="signature-container">
					<span className="signature-header">Your Signature:</span>
					<span className="textfield-container">
						<TextField
							className="text-field"
							id="text-field"
							variant="standard"
							name="signature"
							style={{
								width: "700px",
								borderRadius: 50
							}}
							InputProps={{
								readOnly: true
							}}
							value={member?.first_name + " " + member?.last_name}
						/>
					</span>
					<span className="date-header">Date:</span>
					<span className="date-container">
						<span className="textfield-container">
							<TextField
								className="date-field"
								id="text-field"
								variant="standard"
								placeholder="MM"
								name="month"
								style={{
									width: "30px",
									borderRadius: 50
								}}
								InputProps={{
									readOnly: true
								}}
								value={new Date().getMonth() + 1}
							/>
						</span>
						/
						<span className="textfield-container">
							<TextField
								className="date-field"
								id="text-field"
								variant="standard"
								placeholder="DD"
								name="day"
								style={{
									marginLeft: "3px",
									width: "30px",
									borderRadius: 50
								}}
								value={new Date().getDate()}
								InputProps={{
									readOnly: true
								}}
							/>
						</span>
						/
						<span className="textfield-container">
							<TextField
								className="date-field"
								id="text-field"
								variant="standard"
								placeholder="YYYY"
								name="year"
								style={{
									marginLeft: "3px",
									width: "50px",
									borderRadius: 50
								}}
								value={new Date().getFullYear()}
								InputProps={{
									readOnly: true
								}}
							/>
						</span>
					</span>
				</div>
			</div>
			<div className="theme-plan-section-margin" />
			<PlanActions
				waiveButtonCallback={handleWaiveButtonClick}
				activateButtonCallback={handleActivateEnrollment}
			/>
		</div>
	);
};

export default LincolnForm;
